/*
 * The seven passes of the first read, as the screen shows them before the
 * server has said anything.
 *
 * The server owns this list (`services/orbit_runtime/site_read.py`) and its
 * reports replace these rows by key the moment each pass starts. This copy
 * exists only so the panel has seven dots from the first frame: a read that
 * draws its own shape as it goes reads as a progress bar with no end, and the
 * whole point of naming the passes is that a person can see what is still to
 * come.
 */
import type { DeckReadPass, DeckWork } from './types';

export const READ_PASSES: { key: string; title: string; why: string }[] = [
  { key: 'home', title: 'Homepage and structured data', why: 'What you do, for whom' },
  { key: 'footer', title: 'Footer, legal, contact', why: 'Who you are, where you operate' },
  { key: 'team', title: 'Team page', why: 'Who is behind it' },
  { key: 'offering', title: 'Offering, pricing, case studies', why: 'How you make money, for whom' },
  { key: 'careers', title: 'Careers and open roles', why: 'Teams and growth hide in job posts, held loosely' },
  { key: 'plumbing', title: 'Privacy, subprocessors, site plumbing', why: 'The tools you run on, the honest list' },
  { key: 'news', title: 'News and synthesis', why: 'What you focus on right now' },
];

/*
 * One report from the server, folded into the rows on screen.
 *
 * A report is DATA. It says what a pass is reading and what it found; it never
 * says where the walk should be. Two rules keep it that way:
 *
 *   Rows keep their place.  A report is written into the row with its key, so
 *                           the seven stay in their declared order however out
 *                           of order the server answers.
 *   Nothing regresses.      A pass that has landed stays landed, and a pass
 *                           that found fields keeps them. The server reports
 *                           each pass twice, and the first report carries the
 *                           totals as they were when it STARTED.
 */
const LANDED = ['done', 'empty', 'failed'];

export interface PassReport {
  key: string;
  title: string;
  why: string;
  state: string;
  pages: number;
  paths?: string[];
  found: number;
  line: string;
}

export const mergePass = (rows: DeckReadPass[], report: PassReport): DeckReadPass[] => {
  const at = rows.findIndex((r) => r.key === report.key);
  const row: DeckReadPass = {
    key: report.key,
    title: report.title || READ_PASSES.find((p) => p.key === report.key)?.title || report.key,
    why: report.why,
    state: report.state as DeckReadPass['state'],
    pages: report.pages || 0,
    paths: report.paths,
    found: report.found || 0,
    line: report.line || '',
  };
  if (at < 0) return [...rows, row];
  const had = rows[at];
  if (LANDED.includes(had.state) && !LANDED.includes(row.state)) return rows;
  let paths = had.paths;
  if (row.paths && row.paths.length > 0) paths = row.paths;
  const next = [...rows];
  next[at] = {
    ...row,
    pages: Math.max(had.pages, row.pages),
    paths,
    found: Math.max(had.found, row.found),
    line: row.line || had.line,
  };
  return next;
};

/* The whole read, arriving in one piece rather than pass by pass.

   A browser or proxy that will not stream still gets the answer, and the
   answer carries the same per-pass rows the stream would have sent. They go in
   as data: the sequence keeps its place and the passes it has not reached yet
   show what they found. */
export const fillPasses = (work: DeckWork | null, read: { passes?: PassReport[]; pages_read?: number }): DeckWork | null => {
  if (!work) return work;
  let rows = work.passes || [];
  (read.passes || []).forEach((report) => { rows = mergePass(rows, report); });
  return { ...work, passes: rows, pagesRead: Math.max(work.pagesRead || 0, read.pages_read || 0) };
};

/* A read that stopped. The panel is never taken off the screen mid-pass: the
   sequence closes through its own last card, which says this. */
export const stopWork = (work: DeckWork | null, why: string): DeckWork | null => {
  if (!work) return work;
  return { ...work, failed: why };
};
